import React, {
  forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react'
import { getRegs, oikosGet, update } from '../../../utils/fc'
import { notifyError } from '../../../utils/notify'
import './QuadroPontoAtividades.css'

const COLUNAS = ['Não iniciado', 'Iniciado', 'Concluído']

function listaDe(resposta) {
  const corpo = resposta && resposta.data
  return Array.isArray(corpo) ? corpo : []
}

async function listar(table) {
  const resposta = await oikosGet({ action: 'get', table, data: {} })
  return listaDe(resposta)
}

function mesmoId(a, b) {
  return String(a) === String(b)
}

function normalizar(texto) {
  return String(texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function idSetor(projeto) {
  if (!projeto || projeto.proj_id) return null
  const partes = String(projeto.value || '').split('_')
  if (partes[0] !== 'setor' || !partes[1]) return null
  return partes[1]
}

function tarefasDa(todos, atividadeId) {
  return todos.filter(tarefa => (
    tarefa.todo_tabRef === 'atividade' && mesmoId(tarefa.todo_tabId, atividadeId)
  ))
}

function temAfazerAberto(todos, atividadeId) {
  return tarefasDa(todos, atividadeId).some(tarefa => !Number(tarefa.todo_concluida))
}

const QuadroPontoAtividades = forwardRef(({
  pfId,
  projeto,
  atividadeTexto,
  selecionadas,
  onChangeSelecao,
  onEscolherVinculo,
}, ref) => {
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState('')
  const [atividades, setAtividades] = useState([])
  const [todos, setTodos] = useState([])
  const [setoresAtiv, setSetoresAtiv] = useState([])
  const [produtos, setProdutos] = useState([])
  const [statusLista, setStatusLista] = useState([])
  const [sobre, setSobre] = useState('')
  const [abertos, setAbertos] = useState([])
  const [editandoId, setEditandoId] = useState(null)
  const [nomeEdicao, setNomeEdicao] = useState('')
  const gravandoNome = useRef(false)

  useEffect(() => {
    let vivo = true
    async function carregar() {
      setCarregando(true)
      setErro('')
      try {
        const [ativ, statusAtiv, setores, listaTodo] = await Promise.all([
          getRegs('atividade', { pf_id: Number(pfId) }),
          listar('atividadeStatus'),
          getRegs('setorAtiv'),
          getRegs('todo', { todo_tabRef: 'atividade' }),
        ])
        if (!vivo) return
        if (!Array.isArray(ativ) || !Array.isArray(statusAtiv)) throw new Error('carga')
        setAtividades(ativ.filter(item => Number(item.atividade_ativo) !== 0))
        setStatusLista(statusAtiv)
        setSetoresAtiv(Array.isArray(setores) ? setores : [])
        setTodos(Array.isArray(listaTodo) ? listaTodo : [])
      } catch (e) {
        if (!vivo) return
        setErro('Não foi possível carregar as atividades.')
        notifyError('Não foi possível carregar as atividades.')
      } finally {
        if (vivo) setCarregando(false)
      }
    }
    if (pfId) carregar()
    return () => { vivo = false }
  }, [pfId])

  useEffect(() => {
    let vivo = true
    async function carregarProdutos() {
      if (!projeto || !projeto.proj_id) {
        setProdutos([])
        return
      }
      const lista = await getRegs('prod', { proj_id: Number(projeto.proj_id) })
      if (vivo) setProdutos(Array.isArray(lista) ? lista : [])
    }
    carregarProdutos()
    return () => { vivo = false }
  }, [projeto])

  const statusPorNome = useMemo(() => {
    const mapa = new Map()
    statusLista.forEach(item => mapa.set(normalizar(item.atividadeStatus_nome), item))
    return mapa
  }, [statusLista])

  const colunas = useMemo(() => COLUNAS.map(nome => {
    const status = statusPorNome.get(normalizar(nome))
    return { nome, id: status ? status.atividadeStatus_id : null }
  }), [statusPorNome])

  const visiveis = useMemo(() => {
    if (!projeto) return []
    const setorId = idSetor(projeto)
    const lista = atividades.filter(item => {
      if (projeto.proj_id) return mesmoId(item.proj_id, projeto.proj_id)
      if (!setorId || !item.setorAtiv_id) return false
      const natureza = setoresAtiv.find(setor => mesmoId(setor.setorAtiv_id, item.setorAtiv_id))
      return natureza && mesmoId(natureza.setor_id, setorId)
    })
    return lista.filter(item => item.proj_id || item.setorAtiv_id)
  }, [atividades, projeto, setoresAtiv])

  useEffect(() => {
    const ids = new Set(visiveis.map(item => String(item.atividade_id)))
    let proxima = selecionadas.filter(id => ids.has(String(id)))
    const texto = normalizar(atividadeTexto)
    if (texto && projeto && projeto.proj_id) {
      const produto = produtos.find(item => normalizar(item.prod_desc) === texto)
      if (produto) {
        proxima = proxima.filter(id => {
          const item = atividades.find(atual => mesmoId(atual.atividade_id, id))
          return item && mesmoId(item.prod_id, produto.prod_id)
        })
      }
    }
    if (proxima.length !== selecionadas.length) onChangeSelecao(proxima)
  }, [visiveis, selecionadas, onChangeSelecao, atividadeTexto, projeto, produtos, atividades])

  async function gravarStatus(item, statusId) {
    const anterior = item.atividadeStatus_id
    setAtividades(lista => lista.map(atual => (
      mesmoId(atual.atividade_id, item.atividade_id)
        ? { ...atual, atividadeStatus_id: statusId }
        : atual
    )))
    const gravado = await update('atividade', item.atividade_id, {
      atividadeStatus_id: Number(statusId),
    })
    if (!gravado) {
      setAtividades(lista => lista.map(atual => (
        mesmoId(atual.atividade_id, item.atividade_id)
          ? { ...atual, atividadeStatus_id: anterior }
          : atual
      )))
      notifyError('Não foi possível atualizar a atividade.')
      return false
    }
    return true
  }

  useImperativeHandle(ref, () => ({
    async iniciar(ids) {
      const statusNao = statusPorNome.get('nao iniciado')
      const statusIni = statusPorNome.get('iniciado')
      if (!statusNao || !statusIni) return
      const alvos = atividades.filter(item => (
        ids.some(id => mesmoId(id, item.atividade_id))
        && mesmoId(item.atividadeStatus_id, statusNao.atividadeStatus_id)
      ))
      if (!alvos.length) return
      setAtividades(lista => lista.map(item => (
        alvos.some(alvo => mesmoId(alvo.atividade_id, item.atividade_id))
          ? { ...item, atividadeStatus_id: statusIni.atividadeStatus_id }
          : item
      )))
      const resultados = await Promise.all(alvos.map(item => update('atividade', item.atividade_id, {
        atividadeStatus_id: Number(statusIni.atividadeStatus_id),
      })))
      const falhos = alvos.filter((item, indice) => !resultados[indice])
      if (falhos.length) {
        setAtividades(lista => lista.map(item => (
          falhos.some(alvo => mesmoId(alvo.atividade_id, item.atividade_id))
            ? { ...item, atividadeStatus_id: statusNao.atividadeStatus_id }
            : item
        )))
        notifyError('Não foi possível atualizar a atividade.')
      }
    },
  }), [atividades, statusPorNome])

  async function soltar(event, coluna) {
    event.preventDefault()
    setSobre('')
    if (!coluna.id) return
    const id = event.dataTransfer.getData('text/plain')
    const atual = atividades.find(item => mesmoId(item.atividade_id, id))
    if (!atual || mesmoId(atual.atividadeStatus_id, coluna.id)) return
    if (normalizar(coluna.nome) === 'concluido' && temAfazerAberto(todos, atual.atividade_id)) {
      notifyError('A atividade ainda tem afazeres em aberto.')
      return
    }
    await gravarStatus(atual, coluna.id)
  }

  async function alternarTarefa(tarefa) {
    const vaiConcluir = !Number(tarefa.todo_concluida)
    const outras = tarefasDa(todos, tarefa.todo_tabId)
      .filter(item => !mesmoId(item.todo_id, tarefa.todo_id))
    const ultimoAberto = vaiConcluir && outras.every(item => Number(item.todo_concluida))
    let concluirAtividade = false
    if (ultimoAberto) {
      // eslint-disable-next-line no-alert
      concluirAtividade = window.confirm('Marcar a atividade como concluída?')
    }
    const id = await update('todo', tarefa.todo_id, {
      todo_nome: tarefa.todo_nome,
      todo_concluida: vaiConcluir ? 1 : 0,
      todo_tabRef: 'atividade',
      todo_tabId: Number(tarefa.todo_tabId),
      todo_ativo: 1,
    })
    if (!id) {
      notifyError('Não foi possível atualizar a tarefa.')
      return
    }
    setTodos(lista => lista.map(item => (
      mesmoId(item.todo_id, tarefa.todo_id)
        ? { ...item, todo_concluida: vaiConcluir ? 1 : 0 }
        : item
    )))
    const atividade = atividades.find(item => mesmoId(item.atividade_id, tarefa.todo_tabId))
    if (!atividade) return
    const statusConcluido = statusPorNome.get('concluido')
    const statusIniciado = statusPorNome.get('iniciado')
    const estaConcluida = statusConcluido && mesmoId(atividade.atividadeStatus_id, statusConcluido.atividadeStatus_id)
    if (!vaiConcluir && estaConcluida && statusIniciado) {
      await gravarStatus(atividade, statusIniciado.atividadeStatus_id)
    } else if (concluirAtividade && statusConcluido) {
      await gravarStatus(atividade, statusConcluido.atividadeStatus_id)
    }
  }

  function nomeProduto(item) {
    const produto = produtos.find(atual => mesmoId(atual.prod_id, item.prod_id))
    return produto ? produto.prod_desc : ''
  }

  function mesmoVinculo(origem, destino) {
    if (destino.prod_id) return mesmoId(origem.prod_id, destino.prod_id)
    if (destino.setorAtiv_id) return mesmoId(origem.setorAtiv_id, destino.setorAtiv_id)
    return true
  }

  function alternarSelecao(item) {
    const marcada = selecionadas.some(id => mesmoId(id, item.atividade_id))
    if (marcada) {
      onChangeSelecao(selecionadas.filter(id => !mesmoId(id, item.atividade_id)))
      return
    }
    const restantes = selecionadas.filter(id => {
      const outra = atividades.find(atual => mesmoId(atual.atividade_id, id))
      return outra && mesmoVinculo(outra, item)
    })
    onChangeSelecao([...restantes, item.atividade_id])
    const nome = nomeProduto(item)
    if (nome) onEscolherVinculo(nome)
  }

  function abrirTarefas(atividadeId) {
    setAbertos(lista => (
      lista.some(id => mesmoId(id, atividadeId))
        ? lista.filter(id => !mesmoId(id, atividadeId))
        : [...lista, atividadeId]
    ))
  }

  function iniciarEdicao(tarefa) {
    setEditandoId(tarefa.todo_id)
    setNomeEdicao(tarefa.todo_nome || '')
  }

  async function confirmarNome(event) {
    if (event) event.preventDefault()
    if (gravandoNome.current || editandoId == null) return
    const tarefa = todos.find(item => mesmoId(item.todo_id, editandoId))
    const texto = nomeEdicao.trim()
    if (!tarefa || !texto || texto === tarefa.todo_nome) {
      setEditandoId(null)
      return
    }
    gravandoNome.current = true
    const id = await update('todo', tarefa.todo_id, {
      todo_nome: texto,
      todo_concluida: Number(tarefa.todo_concluida) ? 1 : 0,
      todo_tabRef: 'atividade',
      todo_tabId: Number(tarefa.todo_tabId),
      todo_ativo: 1,
    })
    gravandoNome.current = false
    if (!id) {
      notifyError('Não foi possível atualizar a tarefa.')
      return
    }
    setTodos(lista => lista.map(item => (
      mesmoId(item.todo_id, tarefa.todo_id) ? { ...item, todo_nome: texto } : item
    )))
    setEditandoId(null)
  }

  function cancelarEdicao() {
    gravandoNome.current = true
    setEditandoId(null)
    setNomeEdicao('')
    setTimeout(() => { gravandoNome.current = false }, 0)
  }

  async function excluirTarefa(tarefa) {
    const id = await update('todo', tarefa.todo_id, {
      todo_nome: tarefa.todo_nome,
      todo_concluida: Number(tarefa.todo_concluida) ? 1 : 0,
      todo_tabRef: 'atividade',
      todo_tabId: Number(tarefa.todo_tabId),
      todo_ativo: 0,
    })
    if (!id) {
      notifyError('Não foi possível excluir a tarefa.')
      return
    }
    setTodos(lista => lista.filter(item => !mesmoId(item.todo_id, tarefa.todo_id)))
  }

  if (!projeto) {
    return <p className="ponto-quadro-vazio">Escolha um setor ou projeto.</p>
  }

  if (carregando) {
    return <p className="ponto-quadro-vazio">As atividades estão sendo buscadas.</p>
  }

  if (erro) return <p className="ponto-quadro-erro">{erro}</p>

  return (
    <div className="ponto-quadro">
      {colunas.map(coluna => {
        const itens = visiveis
          .filter(item => mesmoId(item.atividadeStatus_id, coluna.id))
          .sort((a, b) => String(a.atividade_ord || '').localeCompare(String(b.atividade_ord || '')))
        return (
          <section
            key={coluna.nome}
            className={`ponto-coluna${sobre === coluna.nome ? ' is-sobre' : ''}`}
            onDragOver={event => {
              if (coluna.id == null) return
              event.preventDefault()
              setSobre(coluna.nome)
            }}
            onDragLeave={event => {
              if (!event.currentTarget.contains(event.relatedTarget)) setSobre('')
            }}
            onDrop={event => soltar(event, coluna)}
          >
            <header>
              <h3>{coluna.nome}</h3>
              <span>{itens.length}</span>
            </header>
            {!itens.length && <p className="ponto-quadro-vazio">Nenhuma atividade</p>}
            {itens.map(item => {
              const tarefas = tarefasDa(todos, item.atividade_id)
              const marcada = selecionadas.some(id => mesmoId(id, item.atividade_id))
              return (
                <article key={item.atividade_id} className={`ponto-card${marcada ? ' is-sel' : ''}`}>
                  <button
                    type="button"
                    className="ponto-alca"
                    draggable
                    aria-label="Arrastar para mudar o status"
                    onDragStart={event => {
                      const transferencia = event.dataTransfer
                      transferencia.setData('text/plain', String(item.atividade_id))
                      transferencia.effectAllowed = 'move'
                    }}
                  >
                    ⋮⋮
                  </button>
                  <div className="ponto-card-corpo">
                    <button type="button" className="ponto-nome" onClick={() => alternarSelecao(item)}>
                      {item.atividade_nome || 'Sem nome'}
                    </button>
                    {nomeProduto(item) && <span className="ponto-badge">{nomeProduto(item)}</span>}
                    <button
                      type="button"
                      className="ponto-tarefas-titulo"
                      aria-expanded={abertos.some(id => mesmoId(id, item.atividade_id))}
                      onClick={() => abrirTarefas(item.atividade_id)}
                    >
                      Tarefas
                    </button>
                    {abertos.some(id => mesmoId(id, item.atividade_id)) && (
                      <div>
                        {!tarefas.length && <p className="ponto-quadro-vazio">Nenhuma tarefa.</p>}
                        {tarefas.map(tarefa => (
                          <div key={tarefa.todo_id} className={`ponto-tarefa${Number(tarefa.todo_concluida) ? ' concluida' : ''}`}>
                            <input
                              id={`ponto-tarefa-${tarefa.todo_id}`}
                              type="checkbox"
                              checked={Boolean(Number(tarefa.todo_concluida))}
                              aria-label={tarefa.todo_nome || 'Tarefa'}
                              onChange={() => alternarTarefa(tarefa)}
                            />
                            {mesmoId(editandoId, tarefa.todo_id) ? (
                              <form className="ponto-tarefa-edicao" onSubmit={confirmarNome}>
                                <input
                                  type="text"
                                  value={nomeEdicao}
                                  aria-label="Nome da tarefa"
                                  // eslint-disable-next-line jsx-a11y/no-autofocus
                                  autoFocus
                                  onChange={event => setNomeEdicao(event.target.value)}
                                  onBlur={confirmarNome}
                                  onKeyDown={event => {
                                    if (event.key === 'Escape') cancelarEdicao()
                                  }}
                                />
                              </form>
                            ) : (
                              <button type="button" className="ponto-tarefa-nome" onClick={() => iniciarEdicao(tarefa)}>
                                {tarefa.todo_nome}
                              </button>
                            )}
                            <button
                              type="button"
                              className="ponto-tarefa-excluir"
                              aria-label="Excluir tarefa"
                              onClick={() => excluirTarefa(tarefa)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              )
            })}
          </section>
        )
      })}
    </div>
  )
})

QuadroPontoAtividades.displayName = 'QuadroPontoAtividades'

export default QuadroPontoAtividades
